type SendMessageToUnreal = (message: unknown) => void;

export type HotspotPlacementVariant = '2D' | '3D';

export interface HotspotPlacementPayload {
  brandingHotspotId?: string;
  name?: string;
  roomId?: string;
  type?: string;
  data?: {
    url?: string;
    webBrowserUrl?: string;
    [key: string]: unknown;
  };
}

/**
 * Asks UE to start in-world placement of a hotspot.
 *
 * The id is sent twice on purpose: UE's hotspot parser reads it only from
 * `mapSpotId` (or `roomSpotId`), so without the alias it cannot echo the id
 * back in `FINISH_HOTSPOT_PLACE_*` and the hub would have to fall back to
 * "the single active placement session" for correlation.
 *
 * @param sendMessageToUnreal - Bridge that forwards the message to the stream.
 * @param variant - Which placement flow UE should run ('2D' or '3D').
 * @param payload - Hotspot data ridden along and echoed back on finish.
 */
export const startHotspotPlaceInUnreal = (
  sendMessageToUnreal: SendMessageToUnreal,
  variant: HotspotPlacementVariant,
  payload: HotspotPlacementPayload,
) => {
  const { brandingHotspotId } = payload;

  sendMessageToUnreal({
    action:
      variant === '3D' ? 'START_HOTSPOT_PLACE_3D' : 'START_HOTSPOT_PLACE_2D',
    ...payload,
    ...(brandingHotspotId ? { mapSpotId: brandingHotspotId } : {}),
  });
};

/**
 * Nudges UE to re-fetch the authoritative room state after a branding change.
 *
 * @param sendMessageToUnreal - Bridge that forwards the message to the stream.
 */
export const notifyHotspotsUpdatedInUnreal = (
  sendMessageToUnreal: SendMessageToUnreal,
) => {
  sendMessageToUnreal({
    action: 'SPOTS_UPDATED',
    spots: [],
  });
};

/**
 * Points the player's camera at a 3D hotspot's own CameraComponent.
 *
 * UE sends no reply and never times out this out — the caller owns the
 * session and must pair every start with a later
 * {@link endHotspotCameraViewInUnreal} call (hotspot deselected, inspector
 * closed, or the user exits the view) or the player's camera stays locked
 * on the hotspot indefinitely. 2D panel hotspots have no camera component
 * and are out of scope.
 *
 * @param sendMessageToUnreal - Bridge that forwards the message to the stream.
 * @param brandingHotspotId - 3D hotspot whose CameraComponent becomes the view target.
 */
export const startHotspotCameraViewInUnreal = (
  sendMessageToUnreal: SendMessageToUnreal,
  brandingHotspotId: string,
) => {
  sendMessageToUnreal({
    action: 'START_VIEW_HOTSPOT',
    brandingHotspotId,
  });
};

/**
 * Returns the player's camera to their pawn after a hotspot camera view.
 *
 * @param sendMessageToUnreal - Bridge that forwards the message to the stream.
 * @param brandingHotspotId - Hotspot to end the view for; UE falls back to
 * whichever hotspot currently holds the camera when this is omitted.
 */
export const endHotspotCameraViewInUnreal = (
  sendMessageToUnreal: SendMessageToUnreal,
  brandingHotspotId?: string,
) => {
  sendMessageToUnreal({
    action: 'END_VIEW_HOTSPOT',
    ...(brandingHotspotId ? { brandingHotspotId } : {}),
  });
};

/**
 * Selects a hotspot in-world and attaches UE's transform gizmo to it.
 *
 * Sending a START for a different id while one hotspot is already being
 * edited simply switches the gizmo over — no explicit end is required first.
 *
 * @param sendMessageToUnreal - Bridge that forwards the message to the stream.
 * @param brandingHotspotId - Hotspot to select for gizmo editing.
 */
export const startHotspotEditInUnreal = (
  sendMessageToUnreal: SendMessageToUnreal,
  brandingHotspotId: string,
) => {
  sendMessageToUnreal({
    action: 'START_HOTSPOT_EDIT',
    brandingHotspotId,
  });
};

/**
 * Deselects a hotspot and removes UE's transform gizmo from it.
 *
 * @param sendMessageToUnreal - Bridge that forwards the message to the stream.
 * @param brandingHotspotId - Hotspot to end the edit session for; UE ends
 * whatever is currently selected when this is omitted. UE ignores an id that
 * names a different hotspot than the one currently selected (a stale race).
 */
export const endHotspotEditInUnreal = (
  sendMessageToUnreal: SendMessageToUnreal,
  brandingHotspotId?: string,
) => {
  sendMessageToUnreal({
    action: 'END_HOTSPOT_EDIT',
    ...(brandingHotspotId ? { brandingHotspotId } : {}),
  });
};

/**
 * Normalizes the `scale` field of a settled `FINISH_HOTSPOT_PLACE_2D`/`_3D`
 * message (`stages.poi.scale`).
 *
 * A 3-element array — the shape a gizmo scale drag settles as (MV-1850) —
 * passes through untouched so per-axis (non-uniform) scale round-trips. A
 * single-element array collapses to its scalar, matching older UE builds
 * that send `scale: [n]` for a uniform factor; any other shape maps to `undefined`.
 *
 * @param rawScale - The message's `stages.poi.scale` value, of unknown shape.
 */
export const parseFinishedHotspotScale = (
  rawScale: unknown,
): number | number[] | undefined => {
  if (Array.isArray(rawScale)) {
    return rawScale.length === 3 ? (rawScale as number[]) : rawScale[0];
  }
  return typeof rawScale === 'number' ? rawScale : undefined;
};
